import React from 'react';
import DOMPurify from 'dompurify';
import { getBlockStyle, getTextStyle, generateTextStyleClasses, generateTextStyleInlineStyles } from 'utils/blockStyleUtils';
import { containsHtmlTags, convertPlainTextToHtml } from 'utils';
import TooltipWrapper from 'components/shared/TooltipWrapper';

/**
 * BioBlock - Shared component for displaying teacher bio
 *
 * Used in both edit mode (preview) and public mode (full display).
 * Shows the teacher's self-introduction in the current language.
 *
 * @param {Object} props
 * @param {Object} props.content - Content by language object (for edit mode)
 * @param {string} props.bioText - Direct bio text (for public mode)
 * @param {string} props.currentLanguage - Current language code
 * @param {Object} props.pageDesign - Page design configuration
 * @param {Object} props.translations - I18n translations object
 * @param {string} props.mode - Display mode: 'edit' (preview) or 'public' (full display)
 */
export default function BioBlock({
  bioText,
  pageDesign,
  translations,
  mode = 'public'
}) {
  const bio = bioText;

  // Get block style and text styles
  const blockStyle = getBlockStyle(pageDesign, 'bio');
  const contentTextStyle = getTextStyle(blockStyle, 'content');

  const bioTooltipText = translations?.bio_block_tooltip || 'Double-click to edit your bio.';

  // Smart format detection: convert plain text newlines to HTML if no HTML tags present
  const processedBio = bio
    ? (containsHtmlTags(bio) ? bio : convertPlainTextToHtml(bio))
    : '';

  // Sanitize HTML content
  const sanitizedBio = processedBio ? DOMPurify.sanitize(processedBio) : '';

  const bioContent = (
    <>
      {bio ? (
        <div
          className={`bio-text quill-content leading-relaxed text-left break-words ${generateTextStyleClasses(contentTextStyle, pageDesign)}`}
          style={generateTextStyleInlineStyles(contentTextStyle)}
          dangerouslySetInnerHTML={{ __html: sanitizedBio }}
        />
      ) : (
        <p className="text-gray-400 text-sm italic">
          {translations?.block_previews?.bio_placeholder || 'Click to edit and add your introduction'}
        </p>
      )}

      <style jsx>{`
        /* Quill 內容段落樣式 - 與編輯器一致 */
        .bio-text :global(p) {
          margin-bottom: 1em;
        }

        .bio-text :global(p:last-child) {
          margin-bottom: 0;
        }
      `}</style>
    </>
  );

  // Wrap with tooltip in edit mode only
  if (mode === 'edit') {
    return <TooltipWrapper tooltip={bioTooltipText}>{bioContent}</TooltipWrapper>;
  }

  return bioContent;
}
